/**
 * `<CompletedPlanBanner/>` — the top "PLAN COMPLETED" banner (#1043, B8.4 /
 * Track-B, the completed/"archived" plan treatment). Wireframe `V_B84`.
 *
 * Shown across the top of the editorial Taskmap window when the WHOLE board is
 * complete — every outcome's leaves are `done` and there is at least one task
 * (derived, never stored; see `tree/outcomes.isBoardComplete`). It is a calm,
 * factual, GOOD-state banner in the editorial DONE-GREEN tone (`stateStyle
 * ("done")` — the sage dot / soft-green chip), NEVER red or amber.
 *
 * The plan is NOT archived/deleted — the done tree stays visible and read-only
 * for members; a lead can re-open any node (which un-completes the plan and
 * clears this banner). So the copy is a factual capability note, not a warning:
 *   "All N leaves done. This plan is read-only for members; a lead can re-open
 *    any task."
 *
 * `onSnapshotClose` is the wireframe's `snapshot & close` ghost action. It is
 * shown ONLY when the snapshots layer is on (the parent passes the handler then
 * — it reuses the EXISTING snapshot-pin mutation; this component never touches
 * the network) and the surface is the LEAD editor. When omitted (snapshots off,
 * or the member view) the action is absent and only the calm note shows.
 *
 * Pure-presentational + theme-aware: it takes its data (leaf count + the
 * optional handlers) as props, reads `useMapTheme()` for the done-green tone,
 * and is fully testable without the network.
 *
 * Brand firewall: counts WORK ("N leaves done"), never people; done-green; the
 * read-only note is a factual capability statement; burnout never named.
 *
 * Isolation (`taskmap ⊥ scheduler`): taskmap-local + the editorial theme only.
 */

import { useMapTheme } from "../theme/MapThemeContext";

export interface CompletedPlanBannerProps {
  /** Total leaves of work across the board ("All N leaves done."). */
  leafCount: number;
  /**
   * Pin a closing snapshot + (visually) close — fired by the `snapshot & close`
   * ghost action. The parent reuses the EXISTING snapshot-pin mutation. Omitted
   * when the snapshots layer is off OR on the member view ⇒ the action is
   * absent and only the calm note shows. Never archives/deletes nodes.
   */
  onSnapshotClose?: (() => void) | undefined;
  /** True while the closing-snapshot mutation is in flight (disables the action). */
  snapshotPending?: boolean;
  /**
   * The MEMBER read-only variant (#1043, B8.4 §3). Members see the SAME calm
   * done-green note but never the `snapshot & close` action (a lead-only verb);
   * the copy frames it as "read-only for you". Default false (the lead editor).
   */
  member?: boolean;
}

export function CompletedPlanBanner({
  leafCount,
  onSnapshotClose,
  snapshotPending = false,
  member = false,
}: CompletedPlanBannerProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  // The done-GREEN tone (a GOOD state, never red/amber). `done` dot = the
  // saturated sage; chipBg = the soft green fill; fg = the readable green ink.
  const done = theme.stateStyle("done");
  const green = done.dot ?? done.fg;

  // The factual capability note. Members read "read-only for you"; the lead
  // reads "read-only for members" + the re-open affordance reminder.
  const note = member
    ? `All ${leafCount} ${leafCount === 1 ? "leaf is" : "leaves are"} done. This plan is read-only for you; your team's lead can re-open any task.`
    : `All ${leafCount} ${leafCount === 1 ? "leaf" : "leaves"} done. This plan is read-only for members; a lead can re-open any task.`;

  return (
    <div
      data-testid="plan-completed-banner"
      className="flex items-center gap-3 border-b px-5 py-2.5 text-xs"
      style={{
        backgroundColor: done.chipBg,
        borderColor: green,
        color: c.ink,
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      <span
        data-testid="plan-completed-kicker"
        className="shrink-0 font-semibold uppercase"
        style={{
          color: green,
          letterSpacing: "0.08em",
          fontFamily: editorial ? theme.fonts.mono : undefined,
        }}
      >
        ✓ Plan completed
      </span>
      <span data-testid="plan-completed-note" className="flex-1" style={{ color: c.muted }}>
        {note}
      </span>
      {onSnapshotClose && (
        <button
          type="button"
          data-testid="plan-snapshot-close"
          onClick={onSnapshotClose}
          disabled={snapshotPending}
          className="shrink-0 rounded-md border px-3 py-1.5 font-medium transition-colors disabled:opacity-50"
          style={{
            borderColor: green,
            color: green,
            backgroundColor: "transparent",
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
        >
          {snapshotPending ? "pinning…" : "snapshot & close"}
        </button>
      )}
    </div>
  );
}
